import { useCallback, useMemo, useState } from 'react';

const DEFAULT_CONFIRMATION = Object.freeze({
  title: 'Confirm action',
  message: '',
  confirmLabel: 'Continue',
  cancelLabel: 'Cancel',
  busyLabel: 'Working...',
  variant: 'primary',
});

const useConfirmation = () => {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);

  const requestConfirmation = useCallback((options = {}) => {
    setBusy(false);
    setRequest({ ...DEFAULT_CONFIRMATION, ...options });
  }, []);

  const cancelConfirmation = useCallback(() => {
    if (busy) return;
    setRequest(null);
  }, [busy]);

  const confirmAction = useCallback(async () => {
    if (!request || busy) return;
    setBusy(true);

    try {
      await request.onConfirm?.();
    } finally {
      setBusy(false);
      setRequest(null);
    }
  }, [busy, request]);

  const confirmationProps = useMemo(() => ({
    open: Boolean(request),
    title: request?.title || DEFAULT_CONFIRMATION.title,
    message: request?.message || '',
    confirmLabel: request?.confirmLabel || DEFAULT_CONFIRMATION.confirmLabel,
    cancelLabel: request?.cancelLabel || DEFAULT_CONFIRMATION.cancelLabel,
    busyLabel: request?.busyLabel || DEFAULT_CONFIRMATION.busyLabel,
    variant: request?.variant || DEFAULT_CONFIRMATION.variant,
    busy,
    onCancel: cancelConfirmation,
    onConfirm: confirmAction,
  }), [busy, cancelConfirmation, confirmAction, request]);

  return { requestConfirmation, confirmationProps };
};

export default useConfirmation;

